/* eslint-disable */

import { createTheme } from '@mui/material/styles';
import { typography, createSimpleLightPalette as createSimplePalette, customBreakpoints } from './shared';
import * as Colors from './colors';
import Color from 'color';

/*
    Variable color definitions so we can reuse them in the theme overrides below
*/
const ThemeColors = {
    primary: createSimplePalette(Colors.red),
    secondary: createSimplePalette(Colors.blue),
    error: createSimplePalette(Colors.red),
    success: createSimplePalette(Colors.green),
    info: createSimplePalette(Colors.lightBlue),
    divider: Color(Colors.black[500]).alpha(0.12).string(),
    warning: {
        light: Colors.yellow[100],
        main: Colors.yellow[500],
        dark: Colors.yellow[900],
    },
    background: {
        default: Colors.white[200],
        paper: Colors.white[50],
    },
    text: {
        primary: Colors.black[500],
        secondary: Colors.gray[500],
        hint: Colors.gray[500],
    },
    action: {
        active: Colors.gray[500],
        disabled: Color(Colors.black[500]).alpha(0.3).string(),
    },
};
const WhiteText = Colors.white[50];
const BlackBorder = Colors.black[500];
const Spacing = 8;

/*
    Refer to https://mui.com/customization/default-theme/ for a list of properties that are available
    to customize in our themes. These have changed periodically from version to version of Material UI.
*/
export const sapienLightTheme = createTheme({
    direction: 'ltr',
    typography: typography,
    palette: {
        mode: 'light',
        primary: ThemeColors.primary,
        secondary: ThemeColors.secondary,
        error: ThemeColors.error,
        success: ThemeColors.success,
        info: ThemeColors.info,
        divider: ThemeColors.divider,
        warning: ThemeColors.warning,
        background: ThemeColors.background,
        text: ThemeColors.text,
        action: ThemeColors.action,
    },
    breakpoints: customBreakpoints,
    components: {
        // APP BAR OVERRIDES
        MuiAppBar: {
            styleOverrides: {
                colorDefault: {
                    color: ThemeColors.text.primary,
                    backgroundColor: ThemeColors.background.paper,
                },
                colorSecondary: {
                    color: ThemeColors.background.paper,
                    backgroundColor: ThemeColors.primary.dark,
                    '& .MuiInputBase-root': {
                        color: ThemeColors.background.paper,
                    },
                    '& .MuiSelect-icon': {
                        color: ThemeColors.background.paper,
                    },
                },
            },
        },

        // AVATAR OVERRIDES
        MuiAvatar: {
            styleOverrides: {
                colorDefault: {
                    backgroundColor: ThemeColors.primary.light,
                    color: ThemeColors.primary.main,
                },
            },
        },

        // BOTTOM NAVIGATION OVERRIDES
        MuiBottomNavigation: {
            styleOverrides: {
                root: {
                    backgroundColor: ThemeColors.primary.main,
                },
            },
        },

        MuiBottomNavigationAction: {
            styleOverrides: {
                root: {
                    color: Colors.blue[200],
                    '&.Mui-selected': {
                        color: WhiteText,
                        '& .MuiBottomNavigationAction-label': {
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        },
                    },
                },
            },
        },

        // BUTTON OVERRIDES
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
                contained: {
                    backgroundColor: ThemeColors.background.paper,
                    color: ThemeColors.text.primary,
                    '& .MuiButton-disableElevation:not(.MuiButton-containedPrimary):not(.MuiButton-containedSecondary)':
                        {
                            backgroundColor: Colors.white[500],
                            '&:hover': {
                                backgroundColor: Colors.white[300],
                            },
                            '&.Mui-disabled': {
                                borderWidth: 0,
                            },
                        },
                    '&:hover': {
                        backgroundColor: Color(Colors.black[500]).alpha(0.05).string(),
                    },
                    '&.Mui-disabled': {
                        backgroundColor: ThemeColors.background.paper,
                        border: `1px solid ${Color(Colors.black[500]).alpha(0.12).string()}`,
                        color: Color(ThemeColors.text.primary).alpha(0.3).string(),
                    },
                },
                containedPrimary: {
                    backgroundColor: ThemeColors.primary.main,
                    color: WhiteText,
                    '&:hover': {
                        backgroundColor: Colors.blue[300],
                    },
                    '&.Mui-disabled': {
                        backgroundColor: ThemeColors.primary.light,
                        borderWidth: 0,
                        color: Colors.blue[200],
                        opacity: 1,
                    },
                },
                containedSecondary: {
                    backgroundColor: ThemeColors.secondary.main,
                    color: WhiteText,
                    '&:hover': {
                        backgroundColor: Colors.lightBlue[300],
                    },
                    '&.Mui-disabled': {
                        backgroundColor: ThemeColors.secondary.light,
                        borderWidth: 0,
                        color: Colors.lightBlue[200],
                        opacity: 1,
                    },
                },
                outlined: {
                    borderColor: ThemeColors.divider,
                    '&:hover': {
                        backgroundColor: Color(Colors.black[500]).alpha(0.05).string(),
                    },
                    '&.Mui-disabled': {
                        backgroundColor: ThemeColors.background.paper,
                        borderColor: Color(BlackBorder).alpha(0.12).string(),
                        color: Color(ThemeColors.text.primary).alpha(0.3).string(),
                    },
                },
                outlinedPrimary: {
                    borderColor: ThemeColors.primary.main,
                    '&.Mui-disabled': {
                        borderColor: Color(BlackBorder).alpha(0.12).string(),
                    },
                    '&:hover': {
                        backgroundColor: Color(ThemeColors.primary.main).alpha(0.05).string(),
                    },
                },
                outlinedSecondary: {
                    borderColor: ThemeColors.secondary.main,
                    '&.Mui-disabled': {
                        borderColor: Color(BlackBorder).alpha(0.12).string(),
                    },
                    '&:hover': {
                        backgroundColor: Color(ThemeColors.secondary.main).alpha(0.05).string(),
                    },
                },
                text: {
                    '&.Mui-disabled': {
                        color: ThemeColors.action.disabled,
                    },
                    '&:hover': {
                        backgroundColor: Color(Colors.black[500]).alpha(0.05).string(),
                    },
                },
                textPrimary: {
                    '&:hover': {
                        backgroundColor: Color(ThemeColors.primary.main).alpha(0.05).string(),
                    },
                },
                textSecondary: {
                    '&:hover': {
                        backgroundColor: Color(ThemeColors.secondary.main).alpha(0.05).string(),
                    },
                },
            },
        },
        // BUTTON GROUP OVERRIDES
        MuiButtonGroup: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        border: `1px solid ${Color(Colors.black[500]).alpha(0.12).string()}`,
                    },
                },
                groupedOutlinedHorizontal: {
                    '&:not(:last-of-type)': {
                        '&:hover': {
                            borderRightColor: 'transparent',
                        },
                    },
                },
                groupedTextPrimary: {
                    '&:not(:last-child)': {
                        '&.Mui-disabled': {
                            borderColor: ThemeColors.divider,
                        },
                    },
                },
                groupedTextSecondary: {
                    '&:not(:last-child)': {
                        '&.Mui-disabled': {
                            borderColor: ThemeColors.divider,
                        },
                    },
                },
            },
        },

        // CHECKBOX OVERRIDES
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: ThemeColors.action.active,
                },
            },
        },

        // CHIP OVERRIDES
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    backgroundColor: Colors.white[500],
                    color: ThemeColors.text.primary,
                    '& .MuiChip-avatar': {
                        backgroundColor: ThemeColors.primary.main,
                        color: WhiteText,
                        marginRight: -4,
                    },
                    '& .MuiChip-avatarColorPrimary': {
                        backgroundColor: ThemeColors.primary.light,
                        color: ThemeColors.primary.main,
                    },
                    '& .MuiChip-avatarColorSecondary': {
                        backgroundColor: ThemeColors.primary.light,
                        color: ThemeColors.primary.main,
                    },
                    '&.Mui-disabled': {
                        opacity: 1,
                        color: Color(ThemeColors.text.primary).alpha(0.3).string(),
                        '& .MuiChip-avatar': {
                            opacity: 0.5,
                        },
                        '&:not(.MuiChip-colorPrimary):not(.MuiChip-colorSecondary) .MuiChip-deleteIcon': {
                            color: ThemeColors.action.disabled,
                        },
                        '&:not(.MuiChip-colorPrimary):not(.MuiChip-colorSecondary) .MuiChip-icon': {
                            color: ThemeColors.action.disabled,
                        },
                    },
                },
                clickable: {
                    '&:hover': {
                        backgroundColor: Colors.gray[100],
                    },
                    '&.MuiChip-clickableColorPrimary': {
                        '&:hover': {
                            backgroundColor: Colors.blue[300],
                        },
                    },
                    '&.MuiChip-clickableColorSecondary': {
                        '&:hover': {
                            backgroundColor: Colors.lightBlue[300],
                        },
                    },
                },
                colorPrimary: {
                    backgroundColor: ThemeColors.primary.main,
                    color: WhiteText,
                    '&:not(.MuiChip-outlinedPrimary).Mui-disabled': {
                        backgroundColor: ThemeColors.primary.light,
                        color: Colors.blue[200],
                        opacity: 1,
                    },
                },
                colorSecondary: {
                    backgroundColor: ThemeColors.secondary.main,
                    color: WhiteText,
                    '&:not(.MuiChip-outlinedSecondary).Mui-disabled': {
                        backgroundColor: ThemeColors.secondary.light,
                        color: Colors.lightBlue[200],
                        opacity: 1,
                    },
                },
                deleteIcon: {
                    fontSize: '1.125rem',
                    height: '1.125rem',
                    width: '1.125rem',
                    margin: `0px ${Spacing}px 0px -4px`,
                    color: ThemeColors.action.active,
                    '&:hover': {
                        color: ThemeColors.text.primary,
                    },
                    '&.MuiChip-deleteIconColorPrimary': {
                        color: Colors.blue[100],
                        '&:hover': {
                            color: WhiteText,
                        },
                    },
                    '&.MuiChip-deleteIconColorSecondary': {
                        color: Colors.lightBlue[100],
                        '&:hover': {
                            color: WhiteText,
                        },
                    },
                },
                iconColorPrimary: {
                    color: 'inherit',
                },
                iconColorSecondary: {
                    color: 'inherit',
                },
                outlined: {
                    borderColor: Color(BlackBorder).alpha(0.12).string(),
                    backgroundColor: 'transparent',
                    '&.MuiChip-clickable:hover': {
                        backgroundColor: Colors.white[200],
                    },
                    '& .MuiChip-avatar': {
                        backgroundColor: Colors.gray[500],
                        marginRight: -4,
                    },
                    '& .MuiChip-avatarColorPrimary': {
                        backgroundColor: Colors.blue[100],
                        color: ThemeColors.primary.main,
                    },
                    '& .MuiChip-avatarColorSecondary': {
                        backgroundColor: Colors.blue[100],
                        color: ThemeColors.primary.main,
                    },
                    '& .MuiChip-icon': {
                        marginLeft: Spacing,
                        marginRight: -4,
                    },
                    '& .MuiChip-deleteIcon': {
                        margin: `0px ${Spacing}px 0px -4px`,
                    },
                    '&.Mui-disabled .MuiChip-deleteIcon': {
                        color: 'inherit',
                    },
                    '&.MuiChip-outlinedPrimary': {
                        backgroundColor: Color(ThemeColors.primary.main).alpha(0.05).string(),
                        border: `1px solid ${ThemeColors.primary.main}`,
                        color: ThemeColors.primary.main,
                        '&.MuiChip-clickable:hover': {
                            backgroundColor: Color(ThemeColors.primary.main).alpha(0.1).string(),
                        },
                        '&.Mui-disabled': {
                            opacity: 1,
                            backgroundColor: ThemeColors.background.paper,
                            color: ThemeColors.action.disabled,
                            borderColor: Color(BlackBorder).alpha(0.12).string(),
                        },
                        '& .MuiChip-deleteIconOutlinedColorPrimary': {
                            '&:hover': {
                                color: ThemeColors.primary.main,
                            },
                        },
                    },
                    '&.MuiChip-outlinedSecondary': {
                        backgroundColor: Color(ThemeColors.secondary.main).alpha(0.05).string(),
                        border: `1px solid ${ThemeColors.secondary.main}`,
                        color: ThemeColors.secondary.main,
                        '&.MuiChip-clickable:hover': {
                            backgroundColor: Color(ThemeColors.secondary.main).alpha(0.1).string(),
                        },
                        '&.Mui-disabled': {
                            opacity: 1,
                            backgroundColor: ThemeColors.background.paper,
                            color: ThemeColors.action.disabled,
                            borderColor: Color(BlackBorder).alpha(0.12).string(),
                        },
                        '& .MuiChip-deleteIconOutlinedColorSecondary': {
                            '&:hover': {
                                color: ThemeColors.secondary.main,
                            },
                        },
                    },
                },
                icon: {
                    fontSize: '1.125rem',
                    color: ThemeColors.text.primary,
                    marginLeft: Spacing,
                    marginRight: -4,
                },
            },
        },

        // FAB OVERRIDES
        MuiFab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    backgroundColor: ThemeColors.background.paper,
                    color: ThemeColors.text.primary,
                    '&:hover': {
                        backgroundColor: Color(Colors.black[500]).alpha(0.05).string(),
                    },
                    '&.Mui-disabled': {
                        backgroundColor: ThemeColors.background.paper,
                        border: `1px solid ${Color(BlackBorder).alpha(0.12).string()}`,
                        color: ThemeColors.action.disabled,
                    },
                },
                primary: {
                    backgroundColor: ThemeColors.primary.main,
                    color: WhiteText,
                    '&:hover': {
                        backgroundColor: Colors.blue[300],
                    },
                },
                secondary: {
                    backgroundColor: ThemeColors.secondary.main,
                    color: WhiteText,
                    '&:hover': {
                        backgroundColor: Colors.lightBlue[300],
                    },
                },
            },
        },

        // LIST ITEM OVERRIDES
        MuiListItem: {
            styleOverrides: {
                root: {
                    color: ThemeColors.text.primary,
                },
            },
        },

        // MOBILE STEPPER OVERRIDES
        MuiMobileStepper: {
            styleOverrides: {
                dot: {
                    backgroundColor: Colors.gray[200],
                    margin: `0px 4px`,
                },
                dotActive: {
                    backgroundColor: ThemeColors.primary.main,
                },
            },
        },

        // PROGRESS OVERRIDES
        MuiLinearProgress: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: Colors.blue[100],
                },
                colorSecondary: {
                    backgroundColor: Colors.lightBlue[100],
                },
            },
        },

        // SLIDER OVERRIDES
        MuiSlider: {
            styleOverrides: {
                root: {
                    height: 6,
                    color: Colors.blue[300],
                },
                track: {
                    height: 6,
                },
                rail: {
                    height: 4,
                    backgroundColor: Colors.black[100],
                },
                thumb: {
                    backgroundColor: ThemeColors.primary.main,
                },
                thumbColorSecondary: {
                    backgroundColor: ThemeColors.secondary.main,
                },
                mark: {
                    backgroundColor: Colors.blue[200],
                },
                markActive: {
                    backgroundColor: Colors.blue[200],
                },
                valueLabel: {
                    backgroundColor: Colors.blue[300],
                },
                colorSecondary: {
                    color: ThemeColors.secondary.main,
                },
            },
        },

        // SNACKBAR OVERRIDES
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    backgroundColor: Colors.black[900],
                    color: Colors.black[50],
                    '& .MuiButton-textPrimary': {
                        color: Colors.blue[200],
                    },
                    '& .MuiButton-textSecondary': {
                        color: Colors.lightBlue[200],
                    },
                },
            },
        },

        // STEPPER OVERRIDES
        MuiStepConnector: {
            styleOverrides: {
                line: {
                    borderColor: Color(BlackBorder).alpha(0.12).string(),
                },
            },
        },

        MuiStep: {
            styleOverrides: {
                root: {
                    '&.Mui-completed': {
                        '& .MuiStepLabel-iconContainer:before': {
                            content: '""',
                            position: 'absolute',
                            display: 'block',
                            top: '5%',
                            right: '5%',
                            bottom: '5%',
                            left: '5%',
                            backgroundColor: ThemeColors.background.paper,
                            borderRadius: '50%',
                        },
                    },
                },
            },
        },

        MuiStepIcon: {
            styleOverrides: {
                root: {
                    color: Colors.white[500],
                    zIndex: 1,
                    '&.Mui-active': {
                        color: ThemeColors.primary.dark,
                        '& .MuiStepIcon-text': {
                            fill: WhiteText,
                            fontWeight: 600,
                        },
                    },
                },
                text: {
                    fill: ThemeColors.text.primary,
                },
            },
        },

        MuiStepLabel: {
            styleOverrides: {
                label: {
                    color: ThemeColors.text.primary,
                    '&.Mui-active': {
                        fontWeight: 600,
                        color: ThemeColors.primary.main,
                    },
                    '&.Mui-completed': {
                        fontWeight: 600,
                    },
                },
                iconContainer: {
                    position: 'relative',
                },
            },
        },

        // SWITCH OVERRIDES
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    color: ThemeColors.background.paper,
                    '&.Mui-checked + .MuiSwitch-track': {
                        opacity: 0.38,
                    },
                    '&.Mui-checked': {
                        color: ThemeColors.secondary.main,
                        '&.Mui-disabled': {
                            color: Color(ThemeColors.secondary.main)
                                .mix(Color(ThemeColors.background.paper), 0.5)
                                .string(),
                        },
                        '&.Mui-disabled + .MuiSwitch-track': {
                            backgroundColor: ThemeColors.secondary.main,
                        },
                    },
                },
                colorPrimary: {
                    '&.Mui-disabled': {
                        color: ThemeColors.background.paper,
                    },
                    '&.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: Colors.black[100],
                    },
                    '&.Mui-checked': {
                        color: ThemeColors.primary.main,
                        '&.Mui-disabled': {
                            color: Color(ThemeColors.primary.main)
                                .mix(Color(ThemeColors.background.paper), 0.5)
                                .string(),
                        },
                        '&.Mui-disabled + .MuiSwitch-track': {
                            opacity: 0.38,
                            backgroundColor: Color(ThemeColors.primary.main)
                                .mix(Color(ThemeColors.background.paper), 0.5)
                                .string(),
                        },
                    },
                },
                colorSecondary: {
                    '&.Mui-disabled': {
                        color: ThemeColors.background.paper,
                    },
                    '&.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: Colors.black[100],
                    },
                },
                track: {
                    backgroundColor: Colors.black[100],
                    opacity: 0.38,
                },
            },
        },

        // TABLE OVERRIDES
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 600,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    background: ThemeColors.background.paper,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&.MuiTableRow-hover:hover': {
                        backgroundColor: Color(Colors.white[100]).mix(Color(Colors.black[50]), 0.5).string(),
                    },
                    '&.Mui-selected': {
                        backgroundColor: Color(ThemeColors.primary.main).alpha(0.05).string(),
                        '&.MuiTableRow-hover:hover': {
                            backgroundColor: Color(ThemeColors.primary.main).alpha(0.05).string(),
                            '&.MuiTableRow-hover:hover': {
                                backgroundColor: Color(ThemeColors.primary.main).alpha(0.07).string(),
                            },
                        },
                    },
                },
            },
        },

        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        color: ThemeColors.text.primary,
                        '& .MuiTableSortLabel-icon': {
                            color: ThemeColors.text.secondary,
                            opacity: 1,
                        },
                    },
                },
                icon: {
                    opacity: 1,
                    color: Color(Colors.gray[500]).alpha(0.12).string(),
                },
            },
        },

        // TABS OVERRIDES
        MuiTab: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                    '&.Mui-selected': {
                        fontWeight: 600,
                    },
                },
                textColorPrimary: {
                    color: WhiteText,
                    opacity: 0.7,
                    '&.Mui-selected': {
                        color: WhiteText,
                        opacity: 1,
                    },
                },
                textColorSecondary: {
                    color: WhiteText,
                    opacity: 0.7,
                    '&.Mui-selected': {
                        color: WhiteText,
                        opacity: 1,
                    },
                },
            },
        },

        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: ThemeColors.background.paper,
                },
            },
        },

        // TOOLTIP OVERRIDES
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '0.75rem',
                },
            },
        },

        // TEXT FIELD OVERRIDES
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: ThemeColors.action.disabled,
                    },
                },
                input: {
                    '&::placeholder': {
                        color: Colors.black[100],
                        opacity: 1,
                    },
                    '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 30px ${Colors.white[50]} inset`,
                    },
                },
            },
        },

        MuiInput: {
            styleOverrides: {
                underline: {
                    '&:before': {
                        borderBottomColor: ThemeColors.divider,
                    },
                    '&:not(.Mui-disabled):hover:before': {
                        borderBottomWidth: 1,
                        borderBottomColor: BlackBorder,
                    },
                    '&.Mui-disabled:before': {
                        borderBottomColor: ThemeColors.divider,
                        borderBottomStyle: 'solid',
                    },
                    '&.Mui-error:not(.Mui-focused):after': {
                        borderBottomWidth: 1,
                    },
                    '&.Mui-error:not(.Mui-focused)&:hover:after': {
                        borderBottomColor: ThemeColors.error.dark,
                    },
                },
            },
        },

        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: ThemeColors.background.default,
                    '&:hover': {
                        '@media (hover: none)': {
                            backgroundColor: Colors.white[400],
                        },
                        backgroundColor: Colors.white[400],
                    },
                    '&.Mui-focused': {
                        backgroundColor: ThemeColors.background.default,
                    },
                    '&.Mui-disabled': {
                        color: Color(ThemeColors.text.primary).alpha(0.3).string(),
                        backgroundColor: Colors.white[100],
                        pointerEvents: 'none',
                    },
                },
                input: {
                    '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 30px ${ThemeColors.background.default} inset`,
                    },
                },
                underline: {
                    '&:before': {
                        borderBottomColor: ThemeColors.divider,
                    },
                    '&.Mui-error:not(.Mui-focused):after': {
                        borderBottomWidth: 1,
                    },
                    '&.Mui-disabled:before': {
                        borderBottomColor: ThemeColors.divider,
                        borderBottomStyle: 'solid',
                    },
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: ThemeColors.divider,
                    },
                    '&.Mui-error.MuiOutlinedInput-colorSecondary.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: ThemeColors.error.main,
                    },
                    '&.Mui-error:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                        borderColor: ThemeColors.error.dark,
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: ThemeColors.divider,
                    },
                },
                notchedOutline: {
                    borderColor: ThemeColors.divider,
                },
            },
        },

        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: ThemeColors.text.secondary,
                    '&.Mui-disabled': {
                        color: ThemeColors.action.disabled,
                    },
                    '&.MuiFormLabel-filled:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error)': {
                        color: ThemeColors.text.primary,
                    },
                    '&.Mui-error.MuiFormLabel-colorSecondary.Mui-focused': {
                        color: ThemeColors.error.main,
                    },
                },
            },
        },

        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: ThemeColors.action.disabled,
                    },
                },
            },
        },

        // TOGGLE BUTTON OVERRIDES (LAB)
        // @ts-ignore
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    backgroundColor: ThemeColors.background.paper,
                },
                groupedHorizontal: {
                    '&:not(:first-child)': {
                        marginLeft: 0,
                    },
                },
                groupedVertical: {
                    '&:not(:first-child)': {
                        marginTop: 0,
                    },
                },
            },
        },

        // @ts-ignore
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    backgroundColor: ThemeColors.background.paper,
                    color: ThemeColors.text.hint,
                    borderColor: Colors.gray[100],
                    '&:hover': {
                        backgroundColor: Color(Colors.black[500]).alpha(0.05).string(),
                    },
                    '&.Mui-selected': {
                        backgroundColor: ThemeColors.primary.light,
                        color: ThemeColors.primary.main,
                        '&:hover': {
                            backgroundColor: Colors.blue[100],
                        },
                    },
                },
            },
        },
    },
});
